'use client';

/* eslint-disable react/no-unknown-property,
  react/function-component-definition,
  react/jsx-no-constructed-context-values,
  @typescript-eslint/consistent-type-definitions,
  @typescript-eslint/consistent-indexed-object-style */

import { createContext, ReactNode, useContext, useEffect } from 'react';
import { MonarchTestResult } from '../../../../utils/eventing/monarch/types';
import { useMonarchTestResult } from '../../../../utils/eventing/monarch/useMonarchTestResult';

// Define the context type for experiment data
export interface MonarchContextData {
  [experimentKey: string]: MonarchTestResult;
}

// Create context type for experiment data
export const MonarchDataContext = createContext<MonarchContextData>({});

// Hook to use monarch data with optional type parameter
export function useMonarchData<TResult extends MonarchTestResult>(
  experimentKey?: string,
  returnAll?: boolean,
): TResult | MonarchContextData | null {
  const context = useContext(MonarchDataContext);
  // If returnAll is true, return the entire context
  if (returnAll) {
    return context;
  }

  if (!experimentKey || !context[experimentKey]) {
    return null;
  }
  return context[experimentKey] as TResult;
}

// Hook to get the full monarch context
export function useMonarchContext(): MonarchContextData {
  return useContext(MonarchDataContext);
}

// Fallback loader component
const Spinner = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100%',
    }}
  >
    <div
      style={{
        border: '4px solid rgba(0, 0, 0, 0.1)',
        borderLeftColor: '#3498db',
        borderRadius: '50%',
        width: '50px',
        height: '50px',
        animation: 'spin 1s linear infinite',
      }}
    />
    <style jsx>
      {`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}
    </style>
  </div>
);

interface VariantComponentMap {
  // Control variant is required
  control: ReactNode;
  // Other variants are mapped by their variant name
  [key: string]: ReactNode;
}

interface MonarchResolverProps<TResult extends MonarchTestResult> {
  experimentKey: string;
  variants: VariantComponentMap;
  Loader?: ReactNode;
  // Optional fallback variant if the experiment doesn't return a valid variant
  fallbackVariant?: keyof VariantComponentMap;
  // Optional callback to be called with experiment result
  onMonarchTestResolved?: (result: TResult) => void;
}

/**
 * MonarchResolver renders the appropriate variant based on experiment results.
 * It supports multiple variants and provides experiment data through context.
 *
 * @returns {ReactNode} The component for the active variant
 */
function MonarchResolver<TResult extends MonarchTestResult>({
  experimentKey,
  variants,
  Loader = <Spinner />,
  fallbackVariant = 'control' as keyof VariantComponentMap,
  onMonarchTestResolved,
}: MonarchResolverProps<TResult>): ReactNode {
  // Get existing context data
  const existingContextData = useMonarchContext();

  // Get experiment result
  const result = useMonarchTestResult<TResult>(experimentKey);

  // Call the callback if provided
  useEffect(() => {
    if (!result.isLoading && onMonarchTestResolved) {
      onMonarchTestResolved(result);
    }
  }, [result, onMonarchTestResolved]);

  // Show loader while loading
  if (result.isLoading) {
    return Loader;
  }

  // Determine which variant to show
  const variantToShow =
    result.variant && variants[result.variant]
      ? result.variant
      : fallbackVariant;

  // Create context value with the entire test result
  const contextValue = {
    ...existingContextData,
    [experimentKey]: result,
  } as MonarchContextData;

  // Return the component for the active variant wrapped in context
  return (
    <MonarchDataContext.Provider value={contextValue}>
      {variants[variantToShow] ?? variants.control}
    </MonarchDataContext.Provider>
  );
}

export default MonarchResolver;
